import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../modalstyles/CompanyDocumentModal.css';

const API_BASE = 'https://payyug.in/api';
const FILE_BASE = 'https://payyug.in';

const CATEGORY_OPTIONS = [
  'Loan Sanction',
  'Loan Application',
  'Loan Promise',
  'Loan Card',
  'RD Certificate',
  'DD Certificate',
];


const initialFormState = {
  category: '',

  stampFile: null,
  stampPreview: '',
  stampFileName: '',

  signatureFile: null,
  signaturePreview: '',
  signatureFileName: '',
};

const CompanyDocumentModal = ({ onClose }) => {
  const superAdminId = localStorage.getItem('adminuserId');

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addForm, setAddForm] = useState(initialFormState);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ ...initialFormState, id: null });
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  // ── Fetch all documents ──
  const fetchDocuments = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${API_BASE}/list/stamp?superAdminId=${superAdminId}`
      );

      setDocuments(res.data?.data || res.data || []);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (superAdminId) fetchDocuments();
  }, []);

  // ── Filtered & paginated ──
  const filteredDocuments = documents.filter((doc) => {
    const value = `${doc.category}`.toLowerCase();
    return value.includes(search.toLowerCase());
  });

  const totalPages = Math.max(1, Math.ceil(filteredDocuments.length / ITEMS_PER_PAGE));
  const paginatedDocuments = filteredDocuments.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // ── Add form handlers ──
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e, field) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setAddForm((prev) => ({
      ...prev,
      [`${field}File`]: file,
      [`${field}Preview`]: URL.createObjectURL(file),
      [`${field}FileName`]: file.name,
    }));
  };

  const clearForm = () => {
    setAddForm(initialFormState);
    setCurrentPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!addForm.category) {
      alert('Please select category.');
      return;
    }

    if (!addForm.stampFile || !addForm.signatureFile) {
      alert('Please upload both Stamp and Signature.');
      return;
    }

    try {
      const formData = new FormData();

      formData.append('superAdminId', superAdminId);
      formData.append('category', addForm.category);
      formData.append("file", addForm.stampFile);
      formData.append("type", addForm.signatureFile);

      await axios.post(
        `${API_BASE}/create/stamp`,
        formData,
      );

      alert('Document added successfully.');
      clearForm();
      fetchDocuments();
    } catch (err) {
      console.error('Submit error:', err);
      alert(err.response?.data?.message || 'Failed to add document.');
    }
  };

  // ── Edit handlers ──
  const handleEdit = (doc) => {
    setEditForm({
      id: doc.id,
      category: doc.category,

      stampFile: null,
      stampPreview: doc.file
        ? `${FILE_BASE}/${doc.file}`
        : '',
      stampFileName: '',

      signatureFile: null,
      signaturePreview: doc.type
        ? `${FILE_BASE}/${doc.type}`
        : '',
      signatureFileName: '',
    });

    setEditModalOpen(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditImageChange = (e, field) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setEditForm((prev) => ({
      ...prev,
      [`${field}File`]: file,
      [`${field}Preview`]: URL.createObjectURL(file),
      [`${field}FileName`]: file.name,
    }));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    if (!editForm.category) {
    alert('Please select category.');
    return;
  }

    try {
      const formData = new FormData();

      formData.append('superAdminId', superAdminId);
      formData.append('category', editForm.category);
      
      if (editForm.stampFile) {
        formData.append('file', editForm.stampFile);
      }

      if (editForm.signatureFile) {
        formData.append('type', editForm.signatureFile);
      }

      await axios.put(
        `${API_BASE}/document-template/update/${editForm.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      alert('Document updated successfully.');

      setEditModalOpen(false);
      setEditForm({ ...initialFormState, id: null });

      fetchDocuments();
    } catch (err) {
      console.error('Update error:', err);
      alert(err.response?.data?.message || 'Failed to update document.');
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditForm({ ...initialFormState, id: null });
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(
      `${API_BASE}/delete/stamp/${id}`
    );
      alert('Document deleted successfully.');
      fetchDocuments();
    } catch (err) {
      console.error('Delete error:', err);
      alert(err.response?.data?.message || 'Failed to delete document.');
    }
  };

  return (
    <div className="CompanyDocumentModal-backdrop">
      <div className="CompanyDocumentModal-modal">
        <div className="CompanyDocumentModal-header">
          <div>
            <h3>Company Document</h3>
            <p>Add or update company document uploads.</p>
          </div>
          <button className="CompanyDocumentModal-closeBtn" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="CompanyDocumentModal-body">
          {/* ── ADD FORM ── */}
          <form className="CompanyDocumentModal-form" onSubmit={handleSubmit}>
            <label>Loan Agreement</label>
            <select name="category" value={addForm.category} onChange={handleInputChange}>
              <option value="">Select a category</option>
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <label>Stamp Upload</label>

                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={(e) => handleImageChange(e, 'stamp')}
                />

                {addForm.stampPreview && (
                  <div className="CompanyDocumentModal-previewWrapper">
                    <img
                      src={addForm.stampPreview}
                      alt="stamp"
                      className="CompanyDocumentModal-preview"
                    />
                    <span>{addForm.stampFileName}</span>
                  </div>
                )}
                <label>Signature Upload</label>

                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={(e) => handleImageChange(e, 'signature')}
                />

                {addForm.signaturePreview && (
                  <div className="CompanyDocumentModal-previewWrapper">
                    <img
                      src={addForm.signaturePreview}
                      alt="signature"
                      className="CompanyDocumentModal-preview"
                    />
                    <span>{addForm.signatureFileName}</span>
                  </div>
                )}
                
            <div className="CompanyDocumentModal-formButtons">
              <button type="submit" className="CompanyDocumentModal-saveBtn">
                Add Document
              </button>
              <button type="button" className="CompanyDocumentModal-clearBtn" onClick={clearForm}>
                Clear
              </button>
            </div>
          </form>

          {/* ── TABLE ── */}
          <div className="CompanyDocumentModal-tableSection">
            <div className="CompanyDocumentModal-tableHeader">
              <h4>Saved Documents</h4>
              <input
                type="text"
                placeholder="Search documents..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              />
            </div>

            <div className="CompanyDocumentModal-tableWrapper">
              {loading ? (
                <p style={{ textAlign: 'center', padding: '20px' }}>Loading...</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>SN</th>
                      <th>Category</th>
                      <th>Stamp</th>
                      <th>Signature</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDocuments.length > 0 ? (
                      paginatedDocuments.map((doc, index) => (
                        <tr key={doc.id}>
                          <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                          <td>{doc.category}</td>
                          <td>
                            {doc.file && (
                              <img
                                src={`${FILE_BASE}/${doc.file}`}
                                alt="stamp"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "contain"
                                }}
                              />
                            )}
                          </td>

                          <td>
                            {doc.type && (
                              <img
                                src={`${FILE_BASE}/${doc.type}`}
                                alt="signature"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "contain"
                                }}
                              />
                            )}
                          </td>
                          <td>
                            <button className="CompanyDocumentModal-editBtn" onClick={() => handleEdit(doc)}>
                              Edit
                            </button>
                            <button className="CompanyDocumentModal-deleteBtn" onClick={() => handleDelete(doc.id)}>
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5">No documents available.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {totalPages > 1 && (
              <div className="CompanyDocumentModal-pagination">
                <button onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                  Prev
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── EDIT MODAL ── */}
      {editModalOpen && (
        <div className="CompanyDocumentModal-editBackdrop" onClick={closeEditModal}>
          <div className="CompanyDocumentModal-editDialog" onClick={(e) => e.stopPropagation()}>
            <div className="CompanyDocumentModal-editHeader">
              <h4>Edit Company Document</h4>
              <button className="CompanyDocumentModal-closeBtn" onClick={closeEditModal}>
                &times;
              </button>
            </div>
            <form className="CompanyDocumentModal-editForm" onSubmit={handleUpdateSubmit}>
              <label>Loan Agreement</label>
              <select name="category" value={editForm.category} onChange={handleEditInputChange}>
                <option value="">Select a category</option>
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
                <label>Stamp Upload</label>

                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={(e) => handleEditImageChange(e, 'stamp')}
                />

                {editForm.stampPreview && (
                  <div className="CompanyDocumentModal-previewWrapper">
                    <img
                      src={editForm.stampPreview}
                      alt="stamp"
                      className="CompanyDocumentModal-preview"
                    />
                  </div>
                )}

                <label>Signature Upload</label>

                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={(e) => handleEditImageChange(e, 'signature')}
                />

                {editForm.signaturePreview && (
                  <div className="CompanyDocumentModal-previewWrapper">
                    <img
                      src={editForm.signaturePreview}
                      alt="signature"
                      className="CompanyDocumentModal-preview"
                    />
                  </div>
                )}

              <div className="CompanyDocumentModal-formButtons">
                <button type="submit" className="CompanyDocumentModal-saveBtn">
                  Save Changes
                </button>
                <button type="button" className="CompanyDocumentModal-clearBtn" onClick={closeEditModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDocumentModal;